import { Injectable, inject } from '@angular/core';
import { catchError, Observable, switchMap, take, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { HttpResponse } from '../base-http/base-http.type';
import { BaseHttpService } from '../base-http/base-http.service';
import { ImageProcessingProfile } from '../../enums';
import monitoring from 'src/utils/monitoring';

const DEFAULT_HEADERS = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
};

@Injectable({
  providedIn: 'root',
})
export class FileUploadHttpService {
  private _http = inject(HttpClient);
  private _baseHTTP = inject(BaseHttpService);

  private readonly _baseHref = '/api/v1';

  public postBlob(
    url: string,
    file: {
      file: File;
      rotateAngle?: number;
      imageCategory?: string;
      imageProcessingProfile?: ImageProcessingProfile;
    },
  ): Observable<HttpResponse<any>['data']> {
    if (!file.file || file.file.size === 0) {
      const error = new Error('Empty file cannot be uploaded');
      this._reportUploadFailure(error, url, file.file);
      return throwError(() => error);
    }

    const defaultHeaders = { ...DEFAULT_HEADERS };
    // FormData собираем непосредственно перед отправкой (после await заголовков),
    // чтобы не держать «протухшую» ссылку на File через async gap.
    return this._baseHTTP.createHeaders(defaultHeaders).pipe(
      take(1),
      switchMap((headers) => {
        if (!file.file || file.file.size === 0) {
          const error = new Error('Empty file cannot be uploaded');
          this._reportUploadFailure(error, url, file.file);
          return throwError(() => error);
        }

        const data = new FormData();
        data.append('file', file.file, file.file.name);
        data.append('fileType', 'COMMON');
        if (file.rotateAngle) data.append('rotateAngle', file.rotateAngle + '');
        if (file.imageCategory) data.append('imageCategory', file.imageCategory);
        if (file.imageProcessingProfile) {
          data.append('imageProcessingProfile', file.imageProcessingProfile);
        }

        return this._http.post(this._baseHref + url, data, { headers }).pipe(
          catchError((error: HttpErrorResponse) => {
            // this._reportUploadFailure(error, url, file.file);
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  private _reportUploadFailure(error: unknown, url: string, file?: File | null) {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    const message =
      httpError?.message || (error instanceof Error ? error.message : 'File upload failed');

    monitoring.sendMessage(new Error(message), {
      url: `${this._baseHref}${url}`,
      status: httpError?.status,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : undefined,
      standalone:
        typeof window !== 'undefined'
          ? window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as Navigator & { standalone?: boolean }).standalone === true
          : undefined,
    });
  }
}
